import { Autocomplete, Box, Chip, createFilterOptions, TextField } from "@mui/material"
import { BasketUpdate } from "event-definitions"
import { useDialog } from "lib/@hooks/useDialog"
import { keyBy } from "lib/keyBy"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { HelpTag } from "routes/help/helpTag"
import { CreateNewTagDialog } from "routes/schedule/plugins/baskets/tags/create-new-tag-dialog"
import { TagInfo } from "routes/schedule/plugins/baskets/tags/tag-info"
import { tagColor } from "routes/schedule/plugins/baskets/tags/tagColor"
import { useTagsAndAllPossibleTags } from "routes/schedule/plugins/baskets/tags/useTagsAndAllPossibleTags"
import { ScheduleSelectorBeforeList, ScheduleSelectorToolbar } from "slot-definitions"
import "../tree/basket-bar"
import "./tagbar"
import { useState } from "react"
import { setFromValueParam } from "lib/setFromEvent"
import { replaceAllWith } from "lib/@components/slot/replace-all-with"
import { Frag } from "lib/@components/slot/frag"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { notInSelector } from "routes/facilities/lib/when-parent-item"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { addScheduleToTag } from "./controller/add-schedule-to-tag"
import { removeTags } from "./controller/remove-tags"
import { addTag } from "./controller/add-tag"
import { HELPTAG_TAGS } from "routes/help/help-tag-constants"

export const TagUpdated = BasketUpdate("Tag", "*")

const filter = createFilterOptions()

ScheduleSelectorToolbar("basket-Tag").plug(replaceAllWith(<Frag />, 2000))
ScheduleSelectorToolbar("basket-ScheduleBasket").plug(replaceAllWith(<Frag />, 2000))

export function Tags({ referenceId, placeholder = "Enter a tag...", type = "Tag", ...props }) {
    const { schedule } = useBoundContext()
    const openNewTagDialog = useDialog(<CreateNewTagDialog type={type} />)
    const { tags, allTags, loading } = useTagsAndAllPossibleTags(referenceId, type)
    TagUpdated.useRefresh()
    const [inputValue, setInputValue] = useState("")

    if (loading) return <Box height={30} />

    const lookup = keyBy(allTags.baskets, (v) => v.id)

    return (
        <>
            {!tags.length && <Box height={3} />}
            <Autocomplete
                data-cy="schedule-tags-autocomplete"
                className="tags"
                {...props}
                multiple
                freeSolo
                autoHighlight
                inputValue={inputValue}
                onInputChange={setFromValueParam(setInputValue)}
                onChange={handleListChanged}
                value={[...tags].sortBy((t) => t.name).map("id")}
                isOptionEqualToValue={({ id }, value) => id === value}
                disabled={schedule?.retired}
                renderInput={(params) => (
                    <TextField
                        variant="standard"
                        {...params}
                        placeholder={!schedule?.retired ? placeholder : ""}
                        InputProps={{ ...params.InputProps, disableUnderline: true }}
                    />
                )}
                options={[...allTags.baskets.unique("name").filter((c) => c.canEdit !== false)].sort((a, b) =>
                    (a?.name ?? "").localeCompare(b?.name ?? "", undefined, { numeric: true, sensitivity: "base" })
                )}
                getOptionLabel={(v) => (v.name ? v.name : "")}
                filterOptions={filterListOfOptions}
                renderOption={renderTagOption}
                renderTags={(value, getTagProps) =>
                    value.map((tag, index) => {
                        const option = lookup[tag]
                        const tagProps = getTagProps({ index })
                        const [backgroundColor, color] = tagColor(option)
                        return (
                            !!option && (
                                <Chip
                                    {...tagProps}
                                    data-cy="schedule-tag-chip"
                                    sx={{
                                        "&.MuiChip-root": {
                                            color,
                                            backgroundColor: `${backgroundColor} !important`,
                                        },
                                    }}
                                    onDelete={option.canEdit && !schedule?.retired ? tagProps.onDelete : null}
                                    color="primary"
                                    key={option.id}
                                    label={
                                        <Box maxWidth={400}>
                                            <TruncatedTypography>{option.name}</TruncatedTypography>
                                        </Box>
                                    }
                                />
                            )
                        )
                    })
                }
            />
            {!tags.length && <Box height={4} />}
        </>
    )

    async function handleListChanged(_, updatedValue) {
        let toBeResolved = false
        // eslint-disable-next-line no-unused-vars
        const updatedTags = updatedValue
            .map((value) => {
                if (typeof value === "string") return value
                if (value.id) return value.id
                toBeResolved = value.inputValue
                return null
            })
            .filter(Boolean)

        setInputValue("")
        if (toBeResolved) {
            const tagId = await openNewTagDialog({ suggestedTag: toBeResolved })

            if (tagId) {
                await addScheduleToTag(tagId, referenceId)
            }
        } else {
            const existing = new Set(tags.map("id"))
            const newTags = new Set()
            for (const tag of updatedTags) {
                if (!existing.has(tag)) {
                    newTags.add(tag)
                }
                existing.delete(tag)
                setInputValue("")
            }
            try {
                for (const tagId of existing) {
                    await removeTags(tagId, referenceId)
                }
                for (const tagId of newTags) {
                    await addTag(tagId, referenceId)
                }
            } catch (e) {
                errorSnackbar(e.message)
            }
        }
    }

    function filterListOfOptions(options, params) {
        const result = filter(options, params)
        const { inputValue } = params
        const isExisting = allTags.baskets.some((c) => c.name.toLowerCase() === inputValue.toLowerCase())
        if (!isExisting && !!inputValue?.trim()) {
            result.push({ inputValue, name: `Add tag "${inputValue}"` })
        }
        return result
    }

    function renderTagOption(optionProps, option) {
        return (
            <li {...optionProps} data-cy="schedule-tag-option">
                {option.name}
            </li>
        )
    }
}

ScheduleSelectorBeforeList("basket-Tag").plug(<TagInfo if={notInSelector} />)
ScheduleSelectorBeforeList("basket-Tag").plug(<HelpTag tags={[HELPTAG_TAGS]} />)
